import { HttpClient } from '@shared/adapters/HttpClient';
import type {
  LocationStockBatch,
  ProductDetail,
  RelocationDetail,
  RelocationLine,
  ResolvedCart,
  ResolvedLocation,
  ResolvedProduct,
} from '../models';

export interface CreateRelocationPayload {
  cart_id: number;
  notes?: string;
}

export interface CreateRelocationResponse {
  id: number;
}

export interface CreateLinePayload {
  product_id: number;
  from_location_id: number;
  qty: number;
  /** Solo si el stock en ubicación va por lote; omitir para productos sin batch */
  batch_id?: number | null;
}

interface CartApiData {
  id: number;
  code: string;
}

interface CartByIdResponse {
  data: CartApiData;
}

interface CartsSearchResponse {
  data: CartApiData[];
}

interface LocationApiStockBatch {
  batch_id: number;
  product_id: number;
  quantity?: number;
  batch?: {
    batch_number?: string | null;
    expiry_date?: string | null;
    id?: number;
  } | null;
}

interface LocationApiData {
  id: number;
  code: string;
  product_id?: number | null;
  /** Puede ser objeto, array vacío o lista de productos embebidos según endpoint */
  product?: unknown;
  stock?: unknown;
  stock_batches?: LocationApiStockBatch[];
}

interface LocationsSearchResponse {
  data: LocationApiData[];
}

interface ProductApiData {
  id: number;
  code?: string;
  name?: string;
  image?: string;
  image_hash?: string;
}

interface ProductsSearchResponse {
  data: ProductApiData[];
}

function parseLocationStockTotal(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  return null;
}

/** Normaliza `product` como objeto único, array o vacío. */
function mapEmbeddedProductsFromLocation(product: unknown): ResolvedProduct[] {
  if (product == null) return [];
  const items = Array.isArray(product) ? product : [product];
  const out: ResolvedProduct[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object' || !('id' in item)) continue;
    const x = item as Record<string, unknown>;
    const id = Number(x.id);
    if (!Number.isInteger(id) || id <= 0) continue;
    out.push({
      id,
      code: typeof x.code === 'string' ? x.code : undefined,
      name: typeof x.name === 'string' ? x.name : undefined,
      image: typeof x.image === 'string' ? x.image : typeof x.image_hash === 'string' ? x.image_hash : undefined,
    });
  }
  return out;
}

/**
 * Adapter para el flujo de reubicaciones.
 * Cubre crear/consultar la reubicación, agregar líneas, despachar y
 * resolver códigos escaneados contra carritos, ubicaciones y productos.
 */
export class RelocationProcessApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async createRelocation(payload: CreateRelocationPayload): Promise<CreateRelocationResponse> {
    return this.httpClient.post<CreateRelocationResponse>('/relocations', payload);
  }

  async getRelocation(relocationId: number): Promise<RelocationDetail> {
    return this.httpClient.get<RelocationDetail>(`/relocations/${relocationId}`);
  }

  async addLine(relocationId: number, payload: CreateLinePayload): Promise<RelocationLine | null> {
    const body: CreateLinePayload = {
      product_id: payload.product_id,
      from_location_id: payload.from_location_id,
      qty: payload.qty,
    };
    if (payload.batch_id != null && Number.isFinite(payload.batch_id)) {
      body.batch_id = Number(payload.batch_id);
    }
    const response = await this.httpClient.post<RelocationLine | undefined>(`/relocations/${relocationId}/lines`, body);
    return response ?? null;
  }

  async dispatchRelocation(relocationId: number): Promise<void> {
    await this.httpClient.post<unknown>(`/relocations/${relocationId}/dispatch`, {});
  }

  async getProductById(productId: number): Promise<ProductDetail> {
    return this.httpClient.get<ProductDetail>(`/products/${productId}`);
  }

  async getCartById(cartId: number): Promise<ResolvedCart | null> {
    try {
      const response = await this.httpClient.get<CartByIdResponse>(`/carts/${cartId}`);
      if (!response?.data) return null;
      return { id: response.data.id, code: response.data.code };
    } catch (error) {
      console.error('Error cargando carrito', cartId, error);
      return null;
    }
  }

  async findCartByCode(code: string): Promise<ResolvedCart | null> {
    const trimmed = code.trim();
    const response = await this.httpClient.get<CartsSearchResponse>('/carts', {
      search: trimmed,
    });
    const list = response?.data || [];
    const cart = list.find((c) => c.code?.toLowerCase() === trimmed.toLowerCase());
    return cart ? { id: cart.id, code: cart.code } : null;
  }

  async findLocationByCode(code: string): Promise<ResolvedLocation | null> {
    const trimmed = code.trim();
    const response = await this.httpClient.get<LocationsSearchResponse>('/locations', {
      search: trimmed,
    });
    const list = (response?.data || []) as LocationApiData[];
    const location = list.find((l) => l.code?.toLowerCase() === trimmed.toLowerCase());
    if (!location) return null;

    const stock_batches: LocationStockBatch[] | undefined =
      Array.isArray(location.stock_batches) && location.stock_batches.length > 0
        ? location.stock_batches.map((s) => {
            const b = s.batch;
            const batch =
              b && typeof b === 'object'
                ? {
                    batch_number: b.batch_number ?? null,
                    expiry_date: b.expiry_date ?? null,
                  }
                : undefined;
            return {
              batch_id: s.batch_id,
              product_id: s.product_id,
              quantity: s.quantity,
              ...(batch ? { batch } : {}),
            };
          })
        : undefined;

    const stockTotal = parseLocationStockTotal(location.stock);

    const embedded = mapEmbeddedProductsFromLocation(location.product);
    const mergedById = new Map<number, ResolvedProduct>();
    for (const p of embedded) mergedById.set(p.id, p);
    if (stock_batches) {
      for (const s of stock_batches) {
        const pid = s.product_id;
        if (!Number.isInteger(pid) || pid <= 0) continue;
        if (!mergedById.has(pid)) mergedById.set(pid, { id: pid });
      }
    }
    const topPid = location.product_id != null ? Number(location.product_id) : null;
    if (topPid != null && topPid > 0 && !mergedById.has(topPid)) {
      mergedById.set(topPid, { id: topPid });
    }

    const locationProducts = mergedById.size > 0 ? Array.from(mergedById.values()) : undefined;

    return {
      id: location.id,
      code: location.code,
      product_id: location.product_id ?? null,
      stockTotal,
      stock_batches,
      ...(locationProducts ? { locationProducts } : {}),
    };
  }

  async getLocationsByIds(ids: number[]): Promise<ResolvedLocation[]> {
    const normalized = Array.from(new Set(ids.filter((id) => Number.isInteger(id) && id > 0)));
    if (normalized.length === 0) return [];
    const response = await this.httpClient.get<LocationsSearchResponse>('/locations', {
      ids: normalized.join(','),
    });
    const list = response?.data || [];
    return list.map((location) => ({ id: location.id, code: location.code }));
  }

  async findProductByCode(code: string): Promise<ResolvedProduct | null> {
    const trimmed = code.trim();
    const response = await this.httpClient.get<ProductsSearchResponse>('/products', {
      fields: 'basic',
      search: trimmed,
    });
    const list = response?.data || [];
    const product = list.find((p) => p.code?.toLowerCase() === trimmed.toLowerCase()) || list[0] || null;
    if (!product) return null;
    return {
      id: product.id,
      code: product.code,
      name: product.name,
      image: product.image || product.image_hash,
    };
  }
}
